import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import type { DataTableColumn } from '@/components/common/DataTable';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type {
  AvailableBatchDto,
  CreateProductionMaterialIssueRequest,
  ProductionMaterialIssueDto,
  ProductionMaterialIssueLineInput,
  ProductionPickListDto,
} from '@/types/production';

interface LineState {
  qty: number;
  scannedBarcode: string;
  locationId: string | null;
  batchId: string | null;
  // Barcode-wise mode only — true once this line's own physical label has been scanned via the
  // shared Scan Barcode field below (see tryMatchScan). Drives both submit-readiness and the
  // scanned-rows-to-top ordering, same pattern as PutawayPage's AllocationRow.checked.
  checked: boolean;
}

// Mobile-first: single column, large touch targets, minimal chrome — meant to be used from a
// handheld/Android browser on the shop floor, not a desk-bound multi-column layout. A barcode
// scanner (USB/Bluetooth keyboard-wedge, or a phone's camera-scanner app typing into this field)
// works the same way on both desktop and mobile since it's a plain text input, not a camera
// integration — the "scan" is whatever the operator's device already knows how to do.
export default function MaterialIssuePage() {
  const { t } = useTranslation('production');
  const queryClient = useQueryClient();

  const { data: pickLists = [] } = useQuery({
    queryKey: ['production-pick-lists'],
    queryFn: async () => (await apiClient.get<ProductionPickListDto[]>('/production-pick-lists')).data,
  });
  const { data: issues = [], isLoading: loadingIssues } = useQuery({
    queryKey: ['production-material-issues'],
    queryFn: async () => (await apiClient.get<ProductionMaterialIssueDto[]>('/production-material-issues')).data,
  });

  // A pick list must exist and not be fully issued yet — Material Issue can only confirm/post
  // what's already been picked, never resolve stock itself.
  const openPickLists = pickLists.filter((p) => p.status !== 'Completed');

  const [pickListId, setPickListId] = useState('');
  const [lineState, setLineState] = useState<Record<string, LineState>>({});
  const [availableBatches, setAvailableBatches] = useState<Record<string, AvailableBatchDto[]>>({});
  // The one shared scan input for barcode mode — replaces a per-line scan field. Typing/scanning a
  // value that exactly matches a not-yet-checked line's barcode auto-checks that line and clears
  // this field for the next scan (see tryMatchScan). Mirrors PutawayPage's identical pattern.
  const [scanInput, setScanInput] = useState('');
  // Scanned line ids, most-recently-scanned first — drives "scanned rows float to the top" so the
  // operator always sees what's left to scan without hunting through a static list.
  const [scanOrder, setScanOrder] = useState<string[]>([]);

  const { data: config } = useProcessConfiguration();
  const barcodeMandatory = config?.barcodeMandatory ?? false;

  const selectedPickList = openPickLists.find((p) => p.id === pickListId) ?? null;

  const selectPickList = (id: string) => {
    setPickListId(id);
    setScanOrder([]);
    const pickList = openPickLists.find((p) => p.id === id);
    setLineState(
      Object.fromEntries(
        (pickList?.lines ?? []).map((l) => [
          l.id,
          {
            qty: Math.max(l.requiredQty - l.pickedQty, 0), scannedBarcode: '', locationId: l.locationId, batchId: l.batchId,
            // Barcode mode starts every line unconfirmed (must be scanned); without it, there's
            // nothing to scan against, so every line is already "ready" — same rationale as
            // PutawayPage's own checked-default fix.
            checked: !barcodeMandatory,
          },
        ]),
      ),
    );
  };

  // Tries to match `value` against a not-yet-checked line's barcode — on every keystroke (a
  // scanner types the full value near-instantly) and explicitly on Enter (fallback for a scanner
  // that sends Enter, or manual typing). On a match: the line is checked, stamped with the scanned
  // value, and floated to the top; the field clears for the next scan. Mirrors PutawayPage exactly.
  const tryMatchScan = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed || !selectedPickList) {
      return false;
    }
    const matchLine = selectedPickList.lines.find(
      (l) => !lineState[l.id]?.checked && (l.barcode ?? '').toUpperCase() === trimmed,
    );
    if (!matchLine) {
      return false;
    }
    setLineState((prev) => ({ ...prev, [matchLine.id]: { ...prev[matchLine.id], checked: true, scannedBarcode: value.trim() } }));
    setScanOrder((prev) => [matchLine.id, ...prev.filter((id) => id !== matchLine.id)]);
    setScanInput('');
    return true;
  };

  const handleScanInputChange = (value: string) => {
    if (!tryMatchScan(value)) {
      setScanInput(value);
    }
  };

  const handleScanKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryMatchScan(scanInput);
    }
  };

  // Scanned lines float to the top (most-recent first); everything else keeps its original order.
  const orderedLines = useMemo(() => {
    if (!selectedPickList) {
      return [];
    }
    const scannedFirst = scanOrder.map((id) => selectedPickList.lines.find((l) => l.id === id)).filter((l): l is NonNullable<typeof l> => Boolean(l));
    const rest = selectedPickList.lines.filter((l) => !scanOrder.includes(l.id));
    return [...scannedFirst, ...rest];
  }, [selectedPickList, scanOrder]);

  // Under "BatchSelect" mode, a line the pick list left unresolved needs the operator to choose
  // from what's actually on hand — fetched lazily per distinct material once a pick list is picked.
  useEffect(() => {
    if (!selectedPickList || config?.productionMaterialIssueMode !== 'BatchSelect') {
      return;
    }

    const unresolved = selectedPickList.lines.filter((l) => !l.locationId);
    unresolved.forEach((l) => {
      if (availableBatches[l.materialId]) {
        return;
      }
      apiClient
        .get<AvailableBatchDto[]>('/production-material-issues/available-batches', {
          params: { materialId: l.materialId, warehouseId: selectedPickList.warehouseId },
        })
        .then((res) => setAvailableBatches((prev) => ({ ...prev, [l.materialId]: res.data })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPickList, config?.productionMaterialIssueMode]);

  const issueMutation = useMutation({
    mutationFn: () => {
      const payload: CreateProductionMaterialIssueRequest = {
        productionPickListId: pickListId,
        lines: Object.entries(lineState)
          .filter(([, s]) => s.qty > 0)
          .map(([productionPickListLineId, s]): ProductionMaterialIssueLineInput => ({
            productionPickListLineId,
            qty: s.qty,
            scannedBarcode: s.scannedBarcode || null,
            locationId: s.locationId,
            batchId: s.batchId,
          })),
      };
      return apiClient.post('/production-material-issues', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-pick-lists'] });
      queryClient.invalidateQueries({ queryKey: ['production-material-issues'] });
      setPickListId('');
      setLineState({});
    },
  });

  const totalToIssue = useMemo(
    () => Object.values(lineState).reduce((sum, s) => sum + (s.qty > 0 ? s.qty : 0), 0),
    [lineState],
  );

  // Barcode scanning is only ever asked for/required when "Barcode Required" (Process
  // Configuration → Compliance & Workflow) is on — with it off, every line is ready regardless of
  // whether it has a barcode at all (the backend re-validates this exact rule server-side too).
  const allBarcodesReady = useMemo(() => {
    if (!selectedPickList) {
      return false;
    }
    if (!barcodeMandatory) {
      return true;
    }
    return selectedPickList.lines.every((l) => {
      const s = lineState[l.id];
      if (!s || s.qty <= 0) {
        return true;
      }
      return l.barcode ? s.checked : false;
    });
  }, [selectedPickList, lineState, barcodeMandatory]);

  const historyColumns: DataTableColumn<ProductionMaterialIssueDto>[] = [
    { field: 'issueNumber', headerName: t('table.number') },
    { field: 'productionOrderNumber', headerName: t('table.productionOrder'), filterable: true },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'issueDate', headerName: t('table.date'), render: (r) => new Date(r.issueDate).toLocaleString() },
    { field: 'issuedByUserName', headerName: t('table.issuedBy') },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
  ];

  return (
    <>
      <PageHeader title={t('materialIssue.title')} subtitle={t('materialIssue.subtitle')} />

      <Box sx={{ maxWidth: 480, mx: { xs: 0, sm: 'auto' } }}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <SearchableSelect
                label={t('materialIssue.selectPickList')} value={pickListId} onChange={selectPickList} fullWidth size="medium"
                options={openPickLists.map((p) => ({ value: p.id, label: `${p.pickListNumber} — ${p.productionOrderNumber}` }))}
              />

              {config && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {t(`materialIssue.mode.${config.productionMaterialIssueMode}`)}
                </Alert>
              )}

              {selectedPickList && barcodeMandatory && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <TextField
                    label={t('materialIssue.scanBarcode')}
                    value={scanInput}
                    onChange={(e) => handleScanInputChange(e.target.value)}
                    onKeyDown={handleScanKeyDown}
                    helperText={t('materialIssue.scanBarcodeHelper')}
                    fullWidth
                    autoFocus
                  />
                  {/* Live scanned-vs-total counter, same pattern as PutawayPage's. */}
                  <Chip
                    label={t('materialIssue.scannedOfTotal', {
                      scanned: selectedPickList.lines.filter((l) => (lineState[l.id]?.qty ?? 0) > 0 && l.barcode && lineState[l.id]?.checked).length,
                      total: selectedPickList.lines.filter((l) => (lineState[l.id]?.qty ?? 0) > 0 && l.barcode).length,
                    })}
                    color={allBarcodesReady ? 'success' : 'warning'}
                    sx={{ mt: 1.25, fontWeight: 700, minWidth: 92, flexShrink: 0 }}
                  />
                </Stack>
              )}

              {selectedPickList && (
                <Stack spacing={1.5}>
                  {(barcodeMandatory ? orderedLines : selectedPickList.lines).map((line) => {
                    const s = lineState[line.id];
                    const remaining = Math.max(line.requiredQty - line.pickedQty, 0);
                    const needsBatchChoice = config?.productionMaterialIssueMode === 'BatchSelect' && !line.locationId;
                    const candidates = availableBatches[line.materialId] ?? [];
                    const scanned = barcodeMandatory && Boolean(s?.checked) && (s?.qty ?? 0) > 0;
                    return (
                      <Box
                        key={line.id}
                        sx={{
                          p: 1.5, border: 1, borderRadius: 1,
                          borderColor: scanned ? 'success.main' : 'divider',
                          bgcolor: scanned ? 'success.50' : undefined,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            {barcodeMandatory && (line.barcode ? (
                              // Read-only — selection is scan-driven only (see the Scan Barcode field
                              // above); manually ticking a line here is deliberately not possible.
                              <Checkbox size="small" checked={Boolean(s?.checked)} disabled sx={{ mt: -0.5, ml: -1 }} />
                            ) : null)}
                            <Box>
                              <Typography variant="subtitle2">{line.materialCode}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {line.materialName}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {line.locationCode ? `${t('pickList.location')}: ${line.locationCode} · ` : ''}
                                {line.batchNo ? `${t('pickList.batch')}: ${line.batchNo} · ` : ''}
                                {t('pickList.individualQty')}: {remaining}
                                {line.packingQty ? ` (${line.packingQty} ${t('pickList.packingQty')})` : ''}
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip size="small" label={t('materialIssue.remaining', { qty: remaining })} color={remaining > 0 ? 'warning' : 'success'} />
                        </Stack>

                        {needsBatchChoice && (
                          <SearchableSelect
                            label={t('materialIssue.selectBatch')} fullWidth size="small" sx={{ mb: 1.5 }}
                            value={s?.batchId ? `${s.locationId ?? ''}|${s.batchId}` : ''}
                            onChange={(next) => {
                              const [locationId, batchId] = next.split('|');
                              setLineState((prev) => ({ ...prev, [line.id]: { ...prev[line.id], locationId: locationId || null, batchId: batchId || null } }));
                            }}
                            options={candidates.map((c) => ({
                              value: `${c.locationId ?? ''}|${c.batchId ?? ''}`,
                              label: `${c.locationCode ?? '—'} · ${c.batchNo ?? t('materialIssue.noBatch')} · ${c.quantityAvailable}`,
                            }))}
                          />
                        )}

                        {barcodeMandatory && (
                          line.barcode ? (
                            <Typography
                              variant="caption" display="block" sx={{ mb: 1.5 }}
                              color={s?.checked ? 'success.main' : 'text.secondary'}
                            >
                              {t('materialIssue.expectedBarcode', { barcode: line.barcode })}
                              {s?.checked ? ` — ${t('materialIssue.scanned')}` : ''}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="error" display="block" sx={{ mb: 1.5 }}>
                              {t('materialIssue.noBarcodeBlocked')}
                            </Typography>
                          )
                        )}

                        {barcodeMandatory ? (
                          // Fixed to this line's own committed qty — a scanned physical unit is
                          // issued in full, not partially, so there's nothing to edit here.
                          <TextField
                            type="number" label={t('materialIssue.qtyToIssue')} fullWidth size="medium"
                            value={s?.qty ?? 0}
                            slotProps={{ htmlInput: { inputMode: 'decimal', style: { fontSize: '1.1rem' }, readOnly: true } }}
                          />
                        ) : (
                          <TextField
                            type="number" label={t('materialIssue.qtyToIssue')} fullWidth size="medium"
                            value={s?.qty ?? 0}
                            onChange={(e) => setLineState((prev) => ({ ...prev, [line.id]: { ...prev[line.id], qty: Number(e.target.value) } }))}
                            slotProps={{ htmlInput: { inputMode: 'decimal', style: { fontSize: '1.1rem' } } }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}

              <Divider />

              <Button
                variant="contained" size="large" fullWidth
                disabled={!pickListId || totalToIssue <= 0 || !allBarcodesReady || issueMutation.isPending}
                onClick={() => issueMutation.mutate()}
              >
                {issueMutation.isPending ? t('materialIssue.issuing') : t('materialIssue.issueButton')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 1.5 }}>
          {t('materialIssue.historyTitle')}
        </Typography>
        <DataTable columns={historyColumns} rows={issues} loading={loadingIssues} searchable getRowId={(r) => r.id} exportFileName="production-material-issues" />
      </Box>
    </>
  );
}
